import React, { useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import './ContactPage.css';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    error: null,
    loading: false
  });

  const [validationErrors, setValidationErrors] = useState({});

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Validate form data
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      errors.email = 'Invalid email address';
    }
    
    if (!formData.subject.trim()) {
      errors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setFormStatus({
      submitted: false,
      error: null,
      loading: true
    });
    
    try {
      // Get user authentication session for API call
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        throw new Error('You must be logged in to submit the contact form');
      }
      
      // Send form data to API
      const response = await fetch('https://api.rangedat.com/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          timestamp: new Date().toISOString()
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send message');
      }
      
      // Reset form on success
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      
      setFormStatus({
        submitted: true,
        error: null,
        loading: false
      });
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setFormStatus(prev => ({
          ...prev,
          submitted: false
        }));
      }, 5000);
      
    } catch (error) {
      console.error('Contact form submission error:', error);
      setFormStatus({
        submitted: false,
        error: error.message,
        loading: false
      });
    }
  };

  return (
    <div className="contact-page-container">
      <div className="contact-page-content">
        <h1>Contact Us</h1>
        <p className="contact-intro">
          Have questions about RangeDat or need assistance with your account? 
          Fill out the form below and our team will get back to you as soon as possible.
        </p>
        
        {formStatus.submitted && (
          <div className="success-message">
            <h3>Thank you for your message!</h3>
            <p>We've received your inquiry and will respond to you shortly.</p>
          </div>
        )}
        
        {formStatus.error && (
          <div className="error-message">
            <h3>Something went wrong</h3>
            <p>{formStatus.error}</p>
          </div>
        )}
        
        <form className="contact-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Your name"
              className={validationErrors.name ? 'error' : ''}
            />
            {validationErrors.name && <span className="error-text">{validationErrors.name}</span>}
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Your email address"
              className={validationErrors.email ? 'error' : ''}
            />
            {validationErrors.email && <span className="error-text">{validationErrors.email}</span>}
          </div>
          
          <div className="form-group">
            <label htmlFor="subject">Subject</label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="Subject of your message"
              className={validationErrors.subject ? 'error' : ''}
            />
            {validationErrors.subject && <span className="error-text">{validationErrors.subject}</span>}
          </div>
          
          <div className="form-group">
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Your message"
              rows="6"
              className={validationErrors.message ? 'error' : ''}
            ></textarea>
            {validationErrors.message && <span className="error-text">{validationErrors.message}</span>}
          </div>
          
          <button 
            type="submit" 
            className="submit-button"
            disabled={formStatus.loading}
          >
            {formStatus.loading ? 'Sending...' : 'Send Message'}
          </button>
        </form>
        
        <div className="additional-contact">
          <h3>Other Ways to Reach Us</h3>
          <div className="contact-methods">
            <div className="contact-method">
              <i className="fas fa-envelope"></i>
              <p>Email: <a href="mailto:contact@golfdat.com">contact@golfdat.com</a></p>
            </div>
            <div className="contact-method">
              <i className="fas fa-comment"></i>
              <p>For faster response, use our in-app chat support when logged in</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
